/**
 * Procedural grass patch (`WorldObject.kind === 'grass'`) — a small cluster
 * of flat, dark-green blade triangles, always billboarded to face the camera
 * (Y-axis only: the patch stays upright, it doesn't tilt with camera pitch,
 * same convention `WorldObject.rotationY`/`PlayerState.facing` use
 * everywhere else) and animated with a GPU-only wind sway.
 *
 * No texture, no GLB, no per-instance geometry: every grass object in every
 * map, in both the live game and `ultra_map_editor`, shares ONE
 * `BufferGeometry` and ONE `ShaderMaterial` (module-scoped, built exactly
 * once — never per `buildScene()` call, let alone per instance). That's the
 * efficiency case for "spawning a lot of these": a patch costs one small
 * draw call and a handful of vertices, not a texture fetch or a GLTF parse.
 * Deliberately not Blender-authored for the same reason — an imported model
 * with a baked sway animation would need a per-instance `AnimationMixer`
 * ticking every frame, which is the one cost that actually multiplies with
 * instance count; a shared vertex-shader sway does not.
 *
 * Billboarding and wind are both driven by the caller once per frame — see
 * `SceneHandles.updateGrass` (index.ts / ultra_map_editor/main.ts's frame
 * loops call it alongside `updateAmbience`).
 */
import * as THREE from 'three';
import type { WorldObject } from '../types';

/**
 * Local-space blade layout — deliberately hardcoded, not `Math.random()`:
 * the same patch should look identical every time it's built, not vary by
 * load order. `x` spans roughly [-0.5, 0.5] and `h` roughly [0, 1] so
 * `WorldObject.size.w`/`.h` scale directly to metres via `group.scale`.
 * `z` is a tiny jitter (not 0 for every blade) purely so the patch doesn't
 * read as a perfectly flat card during the one frame after a rotation
 * change, before `updateGrass()`'s next billboard update lands.
 */
const BLADE_OFFSETS: ReadonlyArray<{ x: number; z: number; w: number; h: number }> = [
  { x: -0.46, z: 0.03, w: 0.15, h: 0.62 },
  { x: -0.36, z: -0.06, w: 0.17, h: 0.78 },
  { x: -0.26, z: 0.05, w: 0.16, h: 0.68 },
  { x: -0.16, z: -0.04, w: 0.18, h: 0.82 },
  { x: -0.06, z: 0.06, w: 0.17, h: 0.7 },
  { x: 0.04, z: -0.05, w: 0.19, h: 0.85 },
  { x: 0.14, z: 0.04, w: 0.17, h: 0.72 },
  { x: 0.24, z: -0.03, w: 0.16, h: 0.8 },
  { x: 0.34, z: 0.05, w: 0.17, h: 0.66 },
  { x: 0.44, z: -0.02, w: 0.15, h: 0.75 },
  // Two extra blades with bigger z jitter than the front row's — reads as a
  // second, slightly-set-back row for fullness rather than one flat line of
  // blades. Still one shared, module-scoped geometry either way.
  { x: -0.1, z: 0.14, w: 0.16, h: 0.6 },
  { x: 0.1, z: 0.12, w: 0.16, h: 0.64 },
];

const DARK_GREEN = new THREE.Color(0x0c2007);
const TIP_GREEN = new THREE.Color(0x1f4713);

function buildGrassGeometry(): THREE.BufferGeometry {
  const positions: number[] = [];
  const colors: number[] = [];
  for (const b of BLADE_OFFSETS) {
    const left = b.x - b.w / 2;
    const right = b.x + b.w / 2;
    // One triangle per blade — two base corners tapering to a tip point.
    // The cheapest shape that still reads as a blade rather than a card;
    // `side: DoubleSide` on the material means winding order doesn't matter.
    positions.push(left, 0, b.z, right, 0, b.z, b.x, b.h, b.z);
    colors.push(
      DARK_GREEN.r, DARK_GREEN.g, DARK_GREEN.b,
      DARK_GREEN.r, DARK_GREEN.g, DARK_GREEN.b,
      TIP_GREEN.r, TIP_GREEN.g, TIP_GREEN.b,
    );
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return geometry;
}

// Built once, shared forever — see this file's doc comment on why that's
// the whole point. Never disposed, same reasoning as the ground shader's
// module-scoped fallback texture in scene.ts: a module-level resource that
// outlives any one scene/map load.
const GRASS_GEOMETRY = buildGrassGeometry();

const grassUniforms = { uTime: { value: 0 } };

const GRASS_MATERIAL = new THREE.MeshBasicMaterial({
  vertexColors: true,
  side: THREE.DoubleSide,
});
GRASS_MATERIAL.onBeforeCompile = (shader) => {
  Object.assign(shader.uniforms, grassUniforms);
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nuniform float uTime;')
    .replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      // Per-instance phase from the mesh's own world position (modelMatrix),
      // not a per-instance uniform — every patch sways slightly out of step
      // with its neighbours despite all of them sharing this one material.
      // Weighted by transformed.y (0 at the root, up to ~1 at the tip), so
      // blades stay planted and only the tips actually sway.
      float windSeed = modelMatrix[3].x * 0.7 + modelMatrix[3].z * 1.3;
      float sway = sin(uTime * 1.6 + windSeed) * 0.16 * transformed.y;
      transformed.x += sway;`,
    );
};
// Mandatory, not defensive — see scene.ts's ground shader for the same note:
// onBeforeCompile edits are invisible to three's program cache (it keys on
// defines/parameters, not shader source). Without a distinct key, some other
// MeshBasicMaterial could be handed grass's compiled program, or vice versa.
GRASS_MATERIAL.customProgramCacheKey = () => 'grass-wind-v1';

/**
 * `WorldObject.size.w`/`.h`/`.d` scale the shared geometry per instance —
 * same "unit geometry + group.scale" technique `buildZoneMesh`/
 * `buildDecalMesh` use, for the same reason: `TransformControls`' scale mode
 * (and, here, just cheap per-instance sizing) works directly off `.scale`
 * without touching the shared vertex buffer.
 */
export function buildGrassMesh(obj: WorldObject): THREE.Object3D {
  const mesh = new THREE.Mesh(GRASS_GEOMETRY, GRASS_MATERIAL);
  const group = new THREE.Group();
  group.add(mesh);
  group.scale.set(obj.size.w, obj.size.h, obj.size.d);
  return group;
}

/**
 * Advances the shared wind uniform once (regardless of how many patches
 * exist) and billboards every tracked patch to face the camera. Uses ONE
 * shared `cameraYaw` for every instance rather than a per-instance
 * look-at/atan2 toward the camera's actual position — cheaper, and visually
 * indistinguishable for patches clustered near the player/camera target the
 * way decorative grass is. `cameraYaw` follows `WorldObject.rotationY`'s own
 * convention (0 = facing +Z, see minimap-draw.ts's orientation notes) —
 * `mesh.rotation.y = cameraYaw` points each blade's front face in exactly
 * the direction from the target toward the camera.
 */
export function updateGrass(meshes: readonly THREE.Object3D[], dt: number, cameraYaw: number): void {
  grassUniforms.uTime.value += dt;
  for (const m of meshes) m.rotation.y = cameraYaw;
}
